<template>
    <div class="h-100">

        <div class="row h-100">  <!-- GRIDS -->
            <div class="col-12 h-100">

                <div v-if="loadingInitialData">
                    <busy-spinner></busy-spinner>
                </div>

                <div v-else class="h-100 d-flex flex-column">

                    <div class="container">
                        <div class="row">
                            <h6 class="">Select a response item to compare:</h6>
                        </div>
                    </div>

                    <b-tabs
                        no-nav-style
                        lazy 
                        class="flex-fill d-flex flex-column"
                        nav-wrapper-class="d-flex container"
                        nav-class="row spl-floating-nav"
                        content-class="compare-grid-tabs-content flex-fill"
                        active-tab-class="h-100"
                    >  <!-- do not put h-100 on each tab... only on the ACTIVE tab -->
                        <b-tab v-for="todo in toDoListVisible" :key="todo.id" class="d-flex flex-column">
                            <template #title>
                                <div class="d-flex gap-2 align-items-center">
                                    <span>{{ todo.title }}</span>
                                    <div 
                                        v-if="todo.status === 'HIDDEN'"
                                        :id="'todo-hidden-' + todo.id"
                                        v-b-tooltip.hover 
                                        title="Currently hidden from suppliers"
                                        class="font-size-80 d-flex align-items-center text-warning-darker px-1 bg-warning-100 border border-warning rounded-pill"
                                    >
                                        <b-icon icon="eye-slash-fill"></b-icon>
                                    </div>
                                    <div 
                                        v-if="todo.status === 'SUPPLIERLOCKED'"
                                        :id="'todo-supplierlocked-' + todo.id"
                                        v-b-tooltip.hover 
                                        title="Currently locked for suppliers"
                                        class="font-size-80 d-flex align-items-center text-secondary-darker px-1 bg-secondary-100 border border-secondary rounded-pill"
                                    >
                                        <b-icon icon="lock-fill"></b-icon>
                                    </div>
                                </div>
                            </template>

                            <div class="p-3 d-flex justify-content-between align-items-end">
                                <!-- Moved this to compare grid component -->
                            </div>
                            <div class="compare-grid-wrapper flex-fill">
                                
                                <company-todo-compare-grid 
                                    :rctd-id="todo.id"
                                    :only-latest="onlyLatest"
                                    :response-ids="responseIds"
                                    @open-action="handleOpenAction"
                                    class="h-100"
                                >
                                </company-todo-compare-grid>
                            
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>

            </div>
        </div>

        <b-modal
            centered
            size="lg"
            v-model="rfxResponseModal"
            hide-header
            hide-footer
            body-class="p-0"
            @hidden="handleResponseModalHidden"
        >
            <template #default="{ close }">

                <rfx-response-internal-detail
                    ref="rfxResponseInternalDetail"
                    :rfx-response-id="rfxResponseDetailId"
                    :key="rfxResponseDetailId"
                    :initial-tab="rfxResponseDetailTab"
                ></rfx-response-internal-detail>
                <!-- :rtu-role="rtuRole" -->
            
                <!-- Floating Close Button -->
                <b-button
                    variant="outline-white-secondary"
                    class="position-absolute rounded-circle d-flex justify-content-center align-items-center"
                    style="height: 40px; width: 40px; top: -20px; right: -20px;"
                    @click="close()"
                >
                    <b-icon icon="x" font-scale="2.5"></b-icon>
                </b-button>  
            
            </template>

        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import CompanyTodoCompareGrid from '../company_todos/CompanyTodoCompareGrid'
import { errorHandling } from '../../js/custom-vue-mixins';

export default {
    name: 'RfxCompareGrid',
    components: {
        CompanyTodoCompareGrid
    },
    mixins: [
        errorHandling,
    ],
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
        rfxTodoIds: {  // IF PROVIDED, we will ONLY show these RfxCompanyToDo -- we will stil fetch all, but only show these
            type: Array,
            required: false,
        },
        responseIds: {  // IF PROVIDED, we will ONLY show these RfxCompanyToDo -- we actually limit the query on backend
            // we just pass through to the CompanyTodoCompareGrid
            type: Array,
            required: false,
        },
        onlyLatest: {  // if true, we ONLY show the latest for each company -- we actually limit the query on backend
            type: Boolean,
            default: false,
        },
        /* rtuRole: {  // role of the RfxTenantUser - 'admin', 'decision', 'requester'
            type: String,
            required: true,
        }, */
    },
    data: function() {
        return {
            // GETTING RESPONSES AND TODOS
            loadingInitialData: false,  // when fetching inital things needed
            todoList: [],  // fetched during getInitialData
            responseList: [],  // fetched during getInitialData
            // SHOWING RfxResponse MODAL
            rfxResponseModal: false,
            rfxResponseDetailId: null,  // when we set the rfxResponse to show, put the ID here
            rfxResponseDetailTab: '',  // which tab to activate when opening the modal - tabProfile, tabCompanyToDos, internalComments, externalComments, tabTimeline
            vendorResponseMap: new Map(),  // holds mapping of vendor_id: rfx_response_id
        }
    },
    computed: {
        toDoListVisible() {
            // limit to the ones in rfxTodoIds if we have that prop
            if (this.rfxTodoIds && Array.isArray(this.rfxTodoIds)) {
                return this.todoList.filter(td => (this.rfxTodoIds.includes(td.id)));
            } else {
                return this.todoList;
            }
        },
        responseListVisible() {
            // restrict the VISIBLE responses if we passed in responseIds
            if (this.responseIds && Array.isArray(this.responseIds)) {
                return this.responseList.filter(resp => (this.responseIds.includes(resp.id)));
            } else {
                return this.responseList;
            }
        },
    },
    mounted: function() {
        this.getInitialData();
        // don't get form data until after you get initial data... so just call it from getInitialData promise chain
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        getInitialData: function() {
            this.loadingInitialData = true;
            suppliosApi.rfx.compare_grid_get_initial_data(this.rfxPk)
            .then(response => {  // will return data with keys 'responses' and 'todos'
                console.log('got initial data for compare grid');
                console.log(response);
                this.todoList = response.data.todos;
                const responseList = response.data.responses;
                this.responseList = responseList;
                // make a map of vendorId: responseId for easily finding responseId when calling 'openAction' from compare grids
                this.vendorResponseMap = new Map();
                responseList.forEach(resp => {
                    this.vendorResponseMap.set(resp.vendor_id, resp.id);
                });
            })
            .catch(error => this.apiErrorHandler(error, 'Error while getting data for comparison grids'))
            .finally(() => this.loadingInitialData = false)
        },
        showResponseModal: function(responseId, initialTab) {
            // set the ID, then show modal using v-model
            this.rfxResponseDetailId = responseId; 
            this.rfxResponseDetailTab = initialTab ?? '';  // default to empty string
            this.rfxResponseModal = true;
        },
        handleResponseModalHidden: function() {
            this.rfxResponseDetailId = null;  // reset that
            this.rfxResponseDetailTab = '';  // reset to empty string
        },
        handleOpenAction: function(companyId, initialTab) {
            // handles 'open-action' event coming from CompanyTodoCompareGrid (maybe other compare grids)
            // payload should be companyId (string) - changed June 13 2022  (was replyObj before)
            // initialTab (optional) should be a string of which tab to start with in RfxResponseInternalDetail component
            // figure out which RfxResponse to show, then call showResponseModal(responseId)
            const company_id = companyId;
            const response_id = this.vendorResponseMap.get(company_id);
            this.showResponseModal(response_id, initialTab);
        }
    }
}
</script>

<style>
</style>